"use client";

import { useRef, useState } from "react";
import NumberFlow from "@number-flow/react";
import { motion } from "motion/react";
import { Check } from "lucide-react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Sparkles as SparklesComp } from "@/components/ui/sparkles";
import { TimelineContent } from "@/components/ui/timeline-animation";
import { VerticalCutReveal } from "@/components/ui/vertical-cut-reveal";
import { cn } from "@/lib/utils";
import { useBilling } from "@/hooks/useBilling";
import { useToast } from "@/hooks/useToast";
import { billingApi } from "@/api/billing.api";
import { getAppBridge } from "@/api/client";
import { redirectToUrl } from "@/utils/redirect";
import { toErrorMessage } from "@/utils/errorMessage";
import {
  getAddOns,
  getPlanLadder,
  getPlanOption,
  UPGRADE_PRORATION_NOTE,
} from "@/utils/planLadder";
import { useGuarantee } from "@/hooks/useGuarantee";
import {
  GUARANTEE_PRESUBSCRIBE_COPY,
  guaranteeCopyFor,
} from "@/utils/guaranteeCopy";

/**
 * Display copy only. `code` is the API contract and must never change; `name`
 * is the offer name (MONETIZATION_STRATEGY_HORMOZI.md §4) and `shortName` is
 * the rung label used inside CTAs and confirmations, where the full offer name
 * reads badly. Prices/quotas here are fallback marketing copy — the server's
 * `price_quote.prices` and `usage.max_assets_per_month` win at runtime.
 *
 * `price_quote` specifically, NOT `plan.monthly_price`: the quote is the same
 * value `ShopifyBillingService.createAppSubscription` builds the charge from,
 * so the number on the card and the number Shopify asks the merchant to
 * approve cannot diverge. `plan.monthly_price` is only the seeded price and
 * ignores the per-shop price test.
 *
 * Every bullet here must name something that actually ships. Shopify rejects
 * listings that advertise features the app does not have, so a bullet is only
 * allowed if you can point at the code that delivers it. Two things this list
 * deliberately does NOT claim: per-product analytics (the funnel aggregates by
 * event type only — see `usage-analytics.service.ts`) and tiered support
 * routing (support is one mailbox, `support@arspaceplatform.com`, with no
 * ticketing or priority queue behind it).
 */
const plans = [
  {
    name: "The AR Starter Programme",
    shortName: "Launch",
    code: "launch",
    description: "Best for testing AR on your most important products.",
    price: 49,
    includes: [
      "What's included",
      "Up to 15 products in 3D & AR each month",
      "Quick Capture — fast setup from a single product photo",
      "Fast Render — quicker turnaround",
      "Unlimited shopper views on live AR-enabled products",
      "Ideal for hero products, bestsellers, and first AR tests",
      "Upgrade any product to Premium Generation for $15",
      "Direct email support from the team that built it",
      "AR performance dashboard — viewer loads, AR launches, try-ons, and add-to-carts",
    ],
  },
  {
    name: "The AR Sales Engine",
    shortName: "Growth",
    code: "growth",
    description: "Best for most stores ready to make AR part of the catalog.",
    price: 149,
    popular: true,
    badge: "Best value for growing stores",
    includes: [
      "What's included",
      "Up to 60 products in 3D & AR each month",
      "Quick Capture + Fast Render",
      "5 Premium Generations per month included",
      "Premium Generation unlocks Multi-Angle Capture and/or Studio Render",
      "Priority render queue — your generations run ahead of Launch and free",
      "Built for stores that want AR on real selling products",
      "Direct email support from the team that built it",
      "AR performance dashboard — viewer loads, AR launches, try-ons, and add-to-carts",
    ],
  },
  {
    name: "The Full-Catalog Programme",
    shortName: "Scale",
    code: "scale",
    description: "For brands rolling AR out across launches, collections, and larger catalogs.",
    price: 399,
    includes: [
      "What's included",
      "Up to 250 products in 3D & AR each month",
      "Unlimited Premium Generations",
      "Quick Capture and Multi-Angle Capture",
      "Fast Render and Studio Render",
      "All generation combinations included within monthly quota",
      "Highest queue priority — your generations run ahead of every other plan",
      "Best for high-volume merchandising and ongoing AR rollout",
      "Direct email support from the team that built it",
      "AR performance dashboard — viewer loads, AR launches, try-ons, and add-to-carts",
    ],
  },
];

/**
 * Months charged on an annual plan — the other 2 are the discount. Mirrors
 * `ANNUAL_MONTHS_CHARGED` in `shopify-billing.service.ts`, which is where the
 * annual amount is actually derived. Deriving the yearly figure here with the
 * same multiplier rather than storing a second constant means the advertised
 * yearly price cannot drift from the charged one.
 */
const ANNUAL_MONTHS_CHARGED = 10;

/** Copy only — price and availability come from the server. */
const ADD_ONS = [
  {
    kind: "extra_products" as const,
    title: "Extra Product Pack",
    subtitle: "+3 AR products this billing cycle",
  },
  {
    kind: "premium_generation" as const,
    title: "Premium Generation Upgrade",
    subtitle: "Multi-Angle or Studio Render for 1 product",
  },
];

const PricingSwitch = ({ onSwitch }: { onSwitch: (value: string) => void }) => {
  const [selected, setSelected] = useState("0");

  const handleSwitch = (value: string) => {
    setSelected(value);
    onSwitch(value);
  };

  return (
    <div className="flex justify-center">
      <div className="relative z-10 mx-auto flex w-fit rounded-full border border-white/80 bg-white/70 p-1 backdrop-blur">
        <button
          onClick={() => handleSwitch("0")}
          className={cn(
            "relative z-10 h-10 rounded-full px-6 py-1 text-sm font-medium transition-colors md:h-11 md:px-8 md:text-base",
            selected === "0" ? "text-slate-900" : "text-slate-400",
          )}
        >
          {selected === "0" && (
            <motion.span
              layoutId={"pricing-switch"}
              className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200"
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <span className="relative">Monthly</span>
        </button>

        <button
          onClick={() => handleSwitch("1")}
          className={cn(
            "relative z-10 h-10 flex-shrink-0 rounded-full px-6 py-1 text-sm font-medium transition-colors md:h-11 md:px-8 md:text-base",
            selected === "1" ? "text-slate-900" : "text-slate-400",
          )}
        >
          {selected === "1" && (
            <motion.span
              layoutId={"pricing-switch"}
              className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200"
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <span className="relative flex items-center gap-1">
            Yearly
            <span className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
              2 months free
            </span>
          </span>
        </button>
      </div>
    </div>
  );
};

export default function PricingSection4() {
  const [isYearly, setIsYearly] = useState(false);
  const pricingRef = useRef<HTMLDivElement>(null);
  const { plan: planInfo } = useBilling();
  const { guarantee, loading: guaranteeLoading } = useGuarantee();
  const { showError } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [loadingAddOn, setLoadingAddOn] = useState<string | null>(null);

  // The merchant's actual state decides every CTA on this page. Resolved by
  // the backend so the rules live in one place; `getPlanLadder` only fills in
  // for an older backend that does not send it yet.
  const ladder = getPlanLadder(planInfo);
  const addOns = getAddOns(planInfo);
  const hasSubscription = ladder.current_plan_code !== null;

  /**
   * The monthly amount to display for a rung. Prefers the server quote — the
   * same value the Shopify charge is built from — and only falls back to the
   * hardcoded marketing number when the backend sent no usable quote (older
   * backend, cached response, or an unseeded rung, which quotes 0).
   */
  const monthlyPriceFor = (code: string, fallback: number): number => {
    const quoted = planInfo?.price_quote?.prices?.[code];
    return typeof quoted === 'number' && Number.isFinite(quoted) && quoted > 0
      ? quoted
      : fallback;
  };

  // Fail closed. Until the lookup settles we do not know whether this merchant
  // has a frozen target, and the pre-subscribe wording is wrong for someone who
  // does — so render nothing rather than a claim we may have to swap out under
  // their eyes. After it settles, `null` legitimately means "no target yet".
  const guaranteeCopy = guaranteeLoading
    ? null
    : guarantee
      ? guaranteeCopyFor(guarantee)
      : GUARANTEE_PRESUBSCRIBE_COPY;

  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.3,
        duration: 0.5,
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };

  const togglePricingPeriod = (value: string) =>
    setIsYearly(Number.parseInt(value, 10) === 1);

  const handleSubscribe = async (planCode: string) => {
    const option = getPlanOption(ladder, planCode);

    // Belt and braces: the button is already disabled for these, but a plan
    // change is an irreversible full-page Shopify approval, so refuse to open
    // one the merchant did not knowingly ask for even if state is stale.
    if (option && !option.purchasable) return;

    const plan = plans.find((p) => p.code === planCode);

    // A downgrade is the one purchasable action that DESTROYS something the
    // merchant already has: the lower rung's quota is smaller, and Shopify's
    // approval page shows only the new price, never what it costs them. Once
    // approved it cannot be undone in-session. Every other CTA either adds
    // capacity or is already blocked, so this is the only one worth a stop.
    if (option?.action === 'downgrade') {
      const confirmed = window.confirm(
        `Switch to ${plan?.shortName ?? planCode}?\n\n` +
          `This lowers your monthly AR product allowance. Products above the ` +
          `new limit may stop being served in AR, and unused extra-pack ` +
          `credits expire with your current billing period.\n\n` +
          `You'll approve the change on Shopify's page next.`,
      );
      if (!confirmed) return;
    }
    setLoadingPlan(plan?.name || planCode);
    try {
      const { checkout_url } = await billingApi.createCheckoutSession(
        planCode,
        isYearly ? 'annual' : 'monthly',
      );
      if (!checkout_url || typeof checkout_url !== 'string') {
        throw new Error('Checkout URL missing from server response');
      }
      // Shopify's approval page cannot render inside the Admin iframe, so this
      // MUST leave the frame. `redirectToUrl` uses App Bridge
      // Redirect.Action.REMOTE, which is the only thing that works embedded.
      redirectToUrl(checkout_url, getAppBridge());
    } catch (error: unknown) {
      showError(
        toErrorMessage(error, "Failed to create checkout session. Please try again."),
      );
      setLoadingPlan(null);
    }
  };

  /**
   * One-time add-on purchase. Same REMOTE redirect requirement as a plan
   * change — Shopify hosts the approval page for one-time charges too.
   */
  const handleAddOn = async (kind: 'extra_products' | 'premium_generation') => {
    if (!addOns[kind].available) return;
    setLoadingAddOn(kind);
    try {
      const { checkout_url } =
        kind === 'extra_products'
          ? await billingApi.createExtraProductPackCheckout()
          : await billingApi.createPremiumGenerationUpgradeCheckout();
      if (!checkout_url || typeof checkout_url !== 'string') {
        throw new Error('Checkout URL missing from server response');
      }
      redirectToUrl(checkout_url, getAppBridge());
    } catch (error: unknown) {
      showError(toErrorMessage(error, 'Failed to start add-on checkout. Please try again.'));
      setLoadingAddOn(null);
    }
  };

  return (
    <div
      className="relative mx-auto overflow-hidden bg-gradient-to-b from-white via-[#f7f8ff] to-[#e8edff] text-slate-900"
      ref={pricingRef}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_transparent_55%)]" />
      <TimelineContent
        animationNum={4}
        timelineRef={pricingRef}
        customVariants={revealVariants}
        className="pointer-events-none absolute inset-0 h-96 w-screen overflow-hidden"
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffffbf_1px,transparent_1px),linear-gradient(to_bottom,#ffffffbf_1px,transparent_1px)] bg-[size:70px_70px] opacity-20" />
        <SparklesComp
          density={1000}
          direction="bottom"
          speed={0.6}
          color="var(--sparkles-color)"
          className="absolute inset-x-0 bottom-0 h-full w-full"
        />
      </TimelineContent>

      <div className="relative z-10 mx-auto flex max-w-6xl flex-col gap-10 px-6 pb-16 pt-20">
        <article className="relative z-50 mb-6 space-y-4 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-slate-500">
            Billing
          </p>
          <VerticalCutReveal
            splitBy="words"
            staggerDuration={0.15}
            staggerFrom="first"
            containerClassName="justify-center text-4xl font-semibold text-slate-900 md:text-5xl"
          >
            {hasSubscription
              ? "Your plan"
              : "Turn product photos into 3D & AR your shoppers can use today"}
          </VerticalCutReveal>

          <TimelineContent
            as="p"
            animationNum={0}
            timelineRef={pricingRef}
            customVariants={revealVariants}
            className="mx-auto max-w-3xl text-base text-slate-500"
          >
            {hasSubscription
              ? ladder.has_upgrade_available
                ? "You're on a paid plan. Move up when you need more products, or top up this cycle with a one-time add-on below."
                : "You're on our top plan. Need more this cycle? Add a one-time product pack below — no plan change required."
              : "Your first AR-ready product is free — yours to keep, no time limit. Then roll 3D & AR out across your catalog without hiring a 3D team."}
          </TimelineContent>

          <TimelineContent
            as="div"
            animationNum={1}
            timelineRef={pricingRef}
            customVariants={revealVariants}
          >
            <PricingSwitch onSwitch={togglePricingPeriod} />
          </TimelineContent>
        </article>

        <div
          className="absolute inset-x-[10%] top-10 z-0 h-[85%] w-[80%]"
          style={{
            backgroundImage:
              "radial-gradient(circle at center, rgba(139,92,246,0.15) 0%, transparent 65%)",
          }}
        />

        <div className="relative z-10 grid gap-5 md:grid-cols-3">
          {plans.map((plan, index) => {
            const option = getPlanOption(ladder, plan.code);
            const action = option?.action ?? 'subscribe';
            const isCurrent = action === 'current';
            const isUpgrade = action === 'upgrade';
            const purchasable = option?.purchasable ?? true;
            const isLoading = loadingPlan === plan.name;
            // The popular highlight belongs on whatever the merchant should do
            // next, not permanently on Growth. Once subscribed, that is the
            // next rung up; a Scale merchant gets no highlight at all.
            const highlight = hasSubscription
              ? plan.code === ladder.next_plan_code
              : !!plan.popular;

            return (
            <TimelineContent
              key={plan.name}
              as="div"
              animationNum={2 + index}
              timelineRef={pricingRef}
              customVariants={revealVariants}
            >
              <Card
                className={cn(
                  "relative h-full border-white/80 bg-white/90 backdrop-blur-xl",
                  highlight &&
                    "border-indigo-200 shadow-[0_30px_80px_rgba(99,102,241,0.25)]",
                  isCurrent && "border-emerald-200 bg-emerald-50/40",
                )}
              >
                {isCurrent ? (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-lg shadow-emerald-200">
                    Your plan
                  </span>
                ) : (
                  highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-lg shadow-indigo-200">
                      {hasSubscription
                        ? "Recommended next step"
                        : (plan as any).badge || "Most Popular"}
                    </span>
                  )
                )}
                <CardHeader className="space-y-4 text-left">
                  <div className="space-y-1">
                    {/* The rung label stays visible because every server-sent
                        CTA label and Shopify charge name uses it. */}
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                      {plan.shortName}
                    </p>
                    <h3 className="text-2xl font-semibold text-slate-900">{plan.name}</h3>
                    <p className="text-sm text-slate-500">{plan.description}</p>
                  </div>
                  <div className="flex items-baseline gap-2 text-slate-900">
                    <span className="text-4xl font-semibold">$</span>
                    <NumberFlow
                      value={
                        isYearly
                          ? Number(
                              (
                                monthlyPriceFor(plan.code, plan.price) *
                                ANNUAL_MONTHS_CHARGED
                              ).toFixed(2),
                            )
                          : monthlyPriceFor(plan.code, plan.price)
                      }
                      format={{ minimumFractionDigits: 0 }}
                      className="text-4xl font-semibold"
                    />
                    <span className="text-sm text-slate-400">
                      /{isYearly ? "year" : "month"}
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-0">
                  <button
                    onClick={() => handleSubscribe(plan.code)}
                    disabled={!purchasable || isLoading}
                    aria-current={isCurrent ? "true" : undefined}
                    className={cn(
                      "w-full rounded-2xl px-4 py-3 text-base font-semibold transition",
                      isCurrent
                        ? "cursor-default bg-emerald-500/10 text-emerald-700"
                        : highlight
                          ? "bg-gradient-to-r from-indigo-500 to-sky-500 text-white shadow-lg shadow-indigo-200"
                          : "bg-slate-900/5 text-slate-800 hover:bg-slate-900/10",
                      (isLoading || (!purchasable && !isCurrent)) &&
                        "cursor-not-allowed opacity-60",
                    )}
                  >
                    {isLoading
                      ? "Redirecting..."
                      : option?.cta_label ?? `Start with ${plan.shortName}`}
                  </button>

                  {/* Removes the "I'll wait until next month" objection, which
                      in practice means never. Shopify genuinely prorates. */}
                  {isUpgrade && !isLoading && (
                    <p className="-mt-4 text-center text-xs text-slate-500">
                      {UPGRADE_PRORATION_NOTE}
                    </p>
                  )}

                  <div className="space-y-3 border-t border-slate-200/60 pt-4 text-sm">
                    <h4 className="text-sm font-medium text-slate-500">
                      {plan.includes[0]}
                    </h4>
                    <ul className="space-y-2">
                      {plan.includes.slice(1).map((feature, featureIndex) => (
                        <li
                          key={`${plan.name}-${feature}-${featureIndex}`}
                          className="flex items-start gap-3 text-slate-700"
                        >
                          <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-900/5 text-slate-700">
                            <Check className="h-3 w-3" />
                          </span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TimelineContent>
            );
          })}
        </div>

        {/* The AR Live Guarantee (§5). Risk reversal is the single biggest
            conversion lever we have, and our refund cost is GPU seconds, so it
            is stated as loudly as the prices themselves. Deliberately NOT in
            the Shopify App Store listing (§10.5 — listing rules forbid
            guarantees); it lives here, inside the embedded app.

            The target is per-merchant and frozen server-side, so this block
            renders whatever `/api/me/guarantee` committed and never a literal.
            Merchants who have not subscribed have no target yet — they get the
            qualitative version, with no number at all. */}
        {guaranteeCopy && (
        <div className="relative z-10 overflow-hidden rounded-3xl border border-indigo-200 bg-gradient-to-r from-indigo-50/90 to-sky-50/90 px-6 py-6 shadow-[0_20px_60px_rgba(99,102,241,0.15)] backdrop-blur">
          <p className="text-xs uppercase tracking-[0.3em] text-indigo-500">
            The AR Live Guarantee
          </p>
          <p className="mt-3 text-lg font-semibold text-slate-900 md:text-xl">
            {guaranteeCopy.headline}
          </p>
          <p className="mt-2 text-sm text-slate-600">{guaranteeCopy.detail}</p>
          <p className="mt-2 text-sm text-slate-600">
            A 3D agency charges $50&ndash;200 per model, and you still need
            somewhere to put it. We do your whole catalog, and you only stay if
            it works.
          </p>
        </div>
        )}

        {/* Add-ons — one-time charges, independent of the subscription. These
            are the release valve that makes the "every plan change costs a
            full-page approval" constraint survivable, so they must be
            reachable WHILE subscribed, not only before subscribing.

            Rendered AFTER the plan cards on purpose (§11.2 presentation rule):
            the upgrade is always the first option a merchant sees at a wall,
            the pack second and framed as worse per-unit. Free-tier shops are
            not shown packs at all — their only paths are upgrade or stay. */}
        {hasSubscription && (
        <div className="relative z-10 rounded-2xl border border-slate-200/80 bg-white/80 px-6 py-5 backdrop-blur">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
            Still need more this cycle? Second-best option
          </p>
          <p className="mt-2 text-xs text-slate-500">
            One-time purchases that top up your current billing cycle. They don&apos;t
            change your plan and don&apos;t need a plan approval.
          </p>
          {/* Honest unit economics — the pack is a real option, and visibly the
              worse one per product. */}
          <p className="mt-2 text-xs font-medium text-slate-600">
            Worth knowing: a $29 pack works out at $9.67 per product. The AR
            Sales Engine is $149 for 60 products — $2.48 per product.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {ADD_ONS.map((addOn) => {
              const state = addOns[addOn.kind];
              const isLoading = loadingAddOn === addOn.kind;
              return (
                <div
                  key={addOn.kind}
                  className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{addOn.title}</p>
                      <p className="text-xs text-slate-500">{addOn.subtitle}</p>
                    </div>
                    <span className="text-sm font-bold text-slate-900">${state.price}</span>
                  </div>
                  <button
                    onClick={() => handleAddOn(addOn.kind)}
                    disabled={!state.available || isLoading}
                    className={cn(
                      "w-full rounded-xl px-3 py-2 text-sm font-semibold transition",
                      state.available
                        ? "bg-slate-900 text-white hover:bg-slate-800"
                        : "cursor-not-allowed bg-slate-900/5 text-slate-400",
                      isLoading && "cursor-not-allowed opacity-60",
                    )}
                  >
                    {isLoading ? "Redirecting..." : `Buy for $${state.price}`}
                  </button>
                  {!state.available && state.reason && (
                    <p className="text-xs text-slate-400">{state.reason}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Trust line */}
        <p className="relative z-10 text-center text-xs text-slate-400">
          No code changes needed to generate your first product. Install, choose a product, generate, and publish.
        </p>
      </div>
    </div>
  );
}
